import { tracked } from '@glimmer/tracking';
import cloneDeep from 'lodash.clonedeep';

import DacData from '../data/dacdata';
import TopologyData from '../data/topologydata';
import FeatureData from '../data/featuredata';

import ModalHandler from '../models/modal-handler';
import ResistorHandler from '../models/resistor-handler';
import TopologyInputHandler from '../models/topology-input-handler';
import SignalChainHandler from '../models/signal-chain-handler';
import CdpDataHandler from '../models/cdp-data-handler';
import ChartHandler from '../models/chart-handler';
import IsReady from '../models/general/is-ready';

import Constants from '../utils/constants';
import Utils from '../utils/utils';

export default class DebtModel extends IsReady {
  urlHandler = null;
  onModalHandlerCreated = null;
  @tracked modalHandler;
  @tracked chartHandler;
  @tracked cdpDataHandler;
  @tracked resistorHandler;
  @tracked signalChainHandler;
  @tracked topologyInputHandler;
  @tracked selectedTotalErrorType;
  selectionFilterOptions = {};

  constructor(urlHandler, onModalHandlerCreated) {
    super(...arguments);
    this.urlHandler = urlHandler;
    this.onModalHandlerCreated = onModalHandlerCreated;
    this.markAsNotReady();
  }

  get topology() {
    if (!this.topologyInputHandler && !this.topologyInputHandler.current) return {};

    // More flags can be added as necessary
    return {
      hasInvertingAmp: this.signalChainHandler.currentItems.find(
        (item) => item.config.id === Constants.SIGNAL_CHAIN_ITEMS.INVERTING_AMP,
      )
        ? true
        : false,
      hasNonInvertingAmp: this.signalChainHandler.currentItems.find(
        (item) => item.config.id === Constants.SIGNAL_CHAIN_ITEMS.NON_INVERTING_AMP,
      )
        ? true
        : false,
      hasNonInvertingBuffer: this.signalChainHandler.currentItems.find(
        (item) => item.config.id === Constants.SIGNAL_CHAIN_ITEMS.NON_INVERTING_BUFFER,
      )
        ? true
        : false,
      hasBuffer: this.signalChainHandler.currentItems.find(
        (item) => item.config.id === Constants.SIGNAL_CHAIN_ITEMS.BUFFER,
      )
        ? true
        : false,
      hasNonInvertingInput: this.topologyInputHandler.current.find((input) => input.isNonInverting) ? true : false,
    };
  }

  get dacSvgCustomLabelMap() {
    let result = {};

    // Assign output voltage values
    result[Constants.SVG.OUTPUT_VOLTAGE.REFERENCE.ID] = this.signalChainHandler.voltageReferenceValue;
    result[Constants.SVG.OUTPUT_VOLTAGE.DAC.ID] = this.primaryVoltageValue;
    result[Constants.SVG.OUTPUT_VOLTAGE.POSITIVE_DAC.ID] = this.positiveVoltage;
    result[Constants.SVG.OUTPUT_VOLTAGE.NEGATIVE_DAC.ID] = this.negativeVoltage;
    result[Constants.SVG.OUTPUT_VOLTAGE.OUTPUT.ID] = this.fullScaleOutputVoltage;

    // Assign dac name/product page link
    if (this.signalChainHandler.dac.selected) {
      result[Constants.SVG.LINK_TYPES.DAC] = {
        name: this.signalChainHandler.dac.selected.name,
        productPage: this.signalChainHandler.dac.selected.productPage,
      };
    }

    // Assign signal chain names / product page links
    this.signalChainHandler.currentItems
      .filter((item) => item.selected)
      .forEach((item) => {
        result[item.config.id] = {
          name: item.selected.name,
          productPage: item.selected.productPage,
        };
      });

    return result;
  }

  get dacSvgConfigTypes() {
    if (!(this.topologyInputHandler.current && this.topologyInputHandler.current.length)) return [];

    // Get config types based on selected topology inputs
    let result = this.topologyInputHandler.current
      .map((input) => {
        if (input.selectedOption) return `${input.config.id}${input.selectedOption.type}`;
        else return null;
      })
      .filter((configType) => !!configType);

    // Add resistor configuration
    if (
      this.resistorHandler.currentInvertingResistor &&
      this.resistorHandler.currentInvertingResistor.hasInternalResistors
    )
      result.push(Constants.SVG.CONFIG.INTERNAL_INV_RES);
    else if (this.resistorHandler.currentInvertingResistor) result.push(Constants.SVG.CONFIG.EXTERNAL_INV_RES);
    else result.push(Constants.SVG.CONFIG.NO_INV_RES);

    return result;
  }

  get fullScaleOutputVoltage() {
    return Utils.isNumeric(this.primaryVoltageValue)
      ? this.getOutputVoltage(this.primaryVoltageValue)
      : this.getOutputVoltage(this.signalChainHandler.inputVoltage);
  }

  get zeroScaleOutputVoltage() {
    return Utils.isNumeric(this.secondaryVoltageValue)
      ? this.getOutputVoltage(this.secondaryVoltageValue)
      : this.fullScaleOutputVoltage;
  }

  get voltageMap() {
    return this.getVoltageMap(this.signalChainHandler.inputVoltage);
  }

  get primaryVoltage() {
    return Object.keys(this.voltageMap)
      .map((key) => Utils.getItemFromMapWithKey(this.voltageMap, key))
      .find((voltageItem) => voltageItem.isPrimary);
  }

  get primaryVoltageValue() {
    return this.primaryVoltage ? this.primaryVoltage.voltage : null;
  }

  get secondaryVoltage() {
    return Object.keys(this.voltageMap)
      .map((key) => Utils.getItemFromMapWithKey(this.voltageMap, key))
      .find((voltageItem) => !voltageItem.isPrimary);
  }

  get secondaryVoltageValue() {
    return this.secondaryVoltage ? this.secondaryVoltage.voltage : null;
  }

  get positiveVoltage() {
    return this.voltageMap.positive ? this.voltageMap.positive.voltage : null;
  }

  get negativeVoltage() {
    return this.voltageMap.negative ? this.voltageMap.negative.voltage : null;
  }

  /**
   * Helpers
   */

  getVoltageMap(inputVoltage) {
    let voltageMap = {};

    if (!this.signalChainHandler.voltageReference || this.signalChainHandler.voltageReference.isInternal)
      return voltageMap;

    const primaryPathResistor = this.resistorHandler.availableResistors.find(
      (resistor) => resistor.isInPrimaryVoltagePath,
    );
    const secondaryPathResistor = this.resistorHandler.availableResistors.find(
      (resistor) => resistor.isCurrent && resistor.isInSecondaryVoltagePath,
    );

    this.addResistorToVoltageMap(primaryPathResistor, inputVoltage, voltageMap, /*isPrimary=*/ true);
    this.addResistorToVoltageMap(secondaryPathResistor, inputVoltage, voltageMap);

    return voltageMap;
  }

  addResistorToVoltageMap(resistor, inputVoltage, voltageMap, isPrimary = false) {
    if (!resistor) return;
    const voltage =
      resistor.canCalculateAmplifierGain && Utils.isNumeric(resistor.amplifierGain)
        ? inputVoltage * resistor.amplifierGain
        : inputVoltage;
    if (resistor.isPositive) {
      voltageMap[Constants.OUTPUT_VOLTAGE.POSITIVE] = {
        isPrimary: isPrimary,
        voltage: voltage,
      };
    } else {
      voltageMap[Constants.OUTPUT_VOLTAGE.NEGATIVE] = {
        isPrimary: isPrimary,
        voltage: voltage,
      };
    }
  }

  getOutputAmplifierGain() {
    let amplifierGain = null;
    const outputAmplifier = this.signalChainHandler.currentItems.find(
      (item) => item.config.id === Constants.SIGNAL_CHAIN_ITEMS.OUTPUT_AMP,
    );

    // Do not calculate output amplifier's gain, if it does not exist
    if (outputAmplifier) {
      const otherResistorId = outputAmplifier.isInverting
          ? Constants.RESISTORS.INVERTING
          : outputAmplifier.isNonInverting
            ? Constants.RESISTORS.NON_INVERTING
            : 'INVALID',
        otherResistor = this.resistorHandler.availableResistors.find((resistor) => resistor.id === otherResistorId),
        outputResistor = this.resistorHandler.availableResistors.find(
          (resistor) => resistor.id === Constants.RESISTORS.OUTPUT,
        ),
        outputResistorsFound =
          outputResistor && Utils.isNumeric(outputResistor.values.res1) && Utils.isNumeric(outputResistor.values.res2);

      if (outputResistorsFound && otherResistor) {
        // There is an output resistor, calculate the amplifier gain using the other resistor's amplifier gain function
        amplifierGain = otherResistor.getAmplifierGain(outputResistor.values.res1, outputResistor.values.res2);
      } else if (!outputResistorsFound && otherResistor) {
        // There is not an output resistor, the amplifier gain is the other resistor's default amplifier gain
        amplifierGain = otherResistor.getDefaultAmplifierGain();
      }
    }

    return amplifierGain;
  }

  getOutputVoltage(intermediaryVoltage) {
    if (Utils.isNumeric(intermediaryVoltage)) {
      const amplifierGain = this.getOutputAmplifierGain();
      if (Utils.isNumeric(amplifierGain)) return intermediaryVoltage * amplifierGain;
    }

    return intermediaryVoltage;
  }

  /**
   * Handlers
   */

  load(parameterMappingData, signalChainGridData) {
    this.modalHandler = new ModalHandler();
    if (typeof this.onModalHandlerCreated === 'function') {
      this.onModalHandlerCreated(this.modalHandler);
    }

    this.chartHandler = new ChartHandler();
    this.cdpDataHandler = new CdpDataHandler(signalChainGridData, parameterMappingData);
    this.resistorHandler = new ResistorHandler(this.cdpDataHandler.cdpParameters);
    this.signalChainHandler = new SignalChainHandler(this.cdpDataHandler.cdpParameters);
    this.topologyInputHandler = new TopologyInputHandler();

    this.signalChainHandler.load(DacData.dacs);
    this.topologyInputHandler.load(TopologyData.topologyInputs);

    window.toggledFeatures = cloneDeep(FeatureData.toggled);
  }

  handleDacTypeChange(option, dacGridData) {
    this.selectedTotalErrorType = undefined;

    this.chartHandler.handleDacTypeChange();
    this.signalChainHandler.handleDacTypeChange(option, dacGridData);
    this.topologyInputHandler.handleDacTypeChange(option);
    this.resistorHandler.handleDacTypeChange(option, this.signalChainHandler.dac.selected);
    this.topologyInputHandler.handleSelectedDacChange(
      this.signalChainHandler.dac.selected,
      this.signalChainHandler.topologyInputData,
    );
    this.resistorHandler.handleTopologyInputChange(this.topologyInputHandler.current);

    let signalChainItems = option.signalChainItems || [],
      signalChainGridData = {};

    signalChainItems
      .map((signalChainItem) => signalChainItem.pst.id)
      .removeDuplicates()
      .forEach((pstId) => {
        /**
         * Note: we need to make sure that we are not modifying the grid data in other places
         * The grid data is too big to clone
         **/
        if (pstId in this.cdpDataHandler.signalChainGridData)
          signalChainGridData[pstId] = this.cdpDataHandler.signalChainGridData[pstId];
        else console.error(`${pstId} could not be found in the Cdp Signal Chain grid data`);
      });

    this.signalChainHandler.handleSignalChainChange(
      signalChainItems,
      signalChainGridData,
      this.resistorHandler.availableResistors,
    );
    this.signalChainHandler.handleTopologyInputChange(this.topologyInputHandler.current);
  }

  handleDacChange(dac) {
    this.signalChainHandler.handleSelectedDacChange(dac.type);
    this.resistorHandler.handleSelectedDacChange(dac.selected);
    this.topologyInputHandler.handleSelectedDacChange(dac.selected, this.signalChainHandler.topologyInputData);
    this.resistorHandler.handleTopologyInputChange(this.topologyInputHandler.current);
    this.signalChainHandler.handleTopologyInputChange(this.topologyInputHandler.current);
  }

  handleTopologyChange() {
    this.topologyInputHandler.handleTopologyChange(this.signalChainHandler.dac.selected);
    this.resistorHandler.handleTopologyInputChange(this.topologyInputHandler.current);
    this.signalChainHandler.handleTopologyInputChange(this.topologyInputHandler.current);
  }

  updateFilterOptions(key, columns, filters, sortcols) {
    if (this.selectionFilterOptions[key]) {
      this.selectionFilterOptions[key].columns = columns;
      this.selectionFilterOptions[key].filters = filters;
      this.selectionFilterOptions[key].sortColumns = sortcols;
    } else {
      this.selectionFilterOptions[key] = {
        columns: columns,
        filters: filters,
        sortColumns: sortcols,
      };
    }
  }
}
